/**
 * ModalDefaultAlert Component
 *
 * A simple, reusable alert component designed for use inside modals.
 * It displays a centered message with an accompanying error icon,
 * typically used for empty states or no-result scenarios.
 *
 * ## Features:
 * - **Centered Layout**: Horizontally and vertically aligns content within the container.
 * - **Default Message**: Falls back to a standard "No results found" message if none is provided.
 * - **Consistent Styling**: Uses Material UI components and theme spacing for consistent UI.
 * - **Icon Support**: Includes an error icon to visually indicate alert or empty state.
 *
 * ## Props:
 * - `message` (string, optional): Custom message to display in the alert.
 *
 * ## Example Usage:
 * ```tsx
 * <ModalDefaultAlert message="No users match your search" />
 * ```
 *
 * @param {string} [message] - The message displayed in the alert. Defaults to "No results found".
 */

import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { black } from '../../colors'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface ModalDefaultAlertProps {
  message?: string
}

export const ModalDefaultAlert: React.FC<ModalDefaultAlertProps> = ({ message }) => {
  const theme = useTheme()
  return (
    <Box sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
      <ErrorOutlineIcon color="error" />
      <Typography sx={{ ml: 1 }} variant='h6' fontSize={theme.spacing(4)} color={black[100]}>
        {message || 'No results found'}
      </Typography>
    </Box>
  )
}
